import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, Edit, Plus, Trash2, Euro, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSubscription } from "@/hooks/useSubscription";

const planSchema = z.object({
  name: z.string().min(1, "Name ist erforderlich"),
  slug: z.string().min(1, "Slug ist erforderlich"),
  description: z.string().optional(),
  price_monthly: z.number().min(0, "Preis muss positiv sein"),
  price_yearly: z.number().min(0, "Preis muss positiv sein"),
  features: z.array(z.string()).default([]),
  max_customers: z.number().optional(),
  max_stamps_per_month: z.number().optional(),
  max_referrals_per_month: z.number().optional(),
  trial_days: z.number().min(0, "Testphase muss positiv sein").default(14),
  is_active: z.boolean().default(true),
});

type PlanForm = z.infer<typeof planSchema>;

interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number;
  features: any; // JSON type from database
  max_customers: number | null;
  max_stamps_per_month: number | null;
  max_referrals_per_month: number | null;
  trial_days: number;
  is_active: boolean;
  stripe_price_monthly: string | null;
  stripe_price_yearly: string | null;
  usage_count?: number;
}

export function PricingManagementModule() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState("");
  const { toast } = useToast();
  const { createCheckout, subscriptionStatus, getCurrentPlan } = useSubscription();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PlanForm>({
    resolver: zodResolver(planSchema),
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data: plansData, error } = await supabase
        .from("subscription_plans")
        .select(`
          *,
          tenant_subscriptions!inner(count)
        `);

      if (error) throw error;

      // Calculate usage count for each plan
      const plansWithUsage = await Promise.all(
        (plansData || []).map(async (plan) => {
          const { count } = await supabase
            .from("tenant_subscriptions")
            .select("*", { count: "exact", head: true })
            .eq("subscription_plan_id", plan.id);
          
          return { 
            ...plan, 
            features: Array.isArray(plan.features) ? plan.features : [], 
            usage_count: count || 0 
          };
        })
      );

      setPlans(plansWithUsage);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast({
        title: "Fehler",
        description: "Preispläne konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (plan: SubscriptionPlan | null = null) => {
    setEditingPlan(plan);
    if (plan) {
      reset({
        name: plan.name,
        slug: plan.slug,
        description: plan.description || "",
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        features: Array.isArray(plan.features) ? plan.features : [],
        max_customers: plan.max_customers || undefined,
        max_stamps_per_month: plan.max_stamps_per_month || undefined,
        max_referrals_per_month: plan.max_referrals_per_month || undefined,
        trial_days: plan.trial_days,
        is_active: plan.is_active,
      });
      setFeatures(plan.features);
    } else {
      reset();
      setFeatures([]);
    }
    setIsDialogOpen(true);
  };

  const onSubmit = async (data: PlanForm) => {
    try {
      const planData = {
        name: data.name,
        slug: data.slug,
        description: data.description || null,
        features: JSON.stringify(features),
        price_monthly: Number(data.price_monthly),
        price_yearly: Number(data.price_yearly),
        max_customers: data.max_customers || null,
        max_stamps_per_month: data.max_stamps_per_month || null,
        max_referrals_per_month: data.max_referrals_per_month || null,
        trial_days: data.trial_days,
        is_active: data.is_active,
      };

      if (editingPlan) {
        const { error } = await supabase
          .from("subscription_plans")
          .update(planData)
          .eq("id", editingPlan.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("subscription_plans")
          .insert(planData);
        if (error) throw error;
      }

      await fetchPlans();
      setIsDialogOpen(false);
      toast({
        title: "Erfolgreich",
        description: editingPlan ? "Preisplan aktualisiert." : "Preisplan erstellt.",
      });
    } catch (error) {
      console.error("Error saving plan:", error);
      toast({
        title: "Fehler",
        description: "Preisplan konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const deletePlan = async (planId: string) => {
    try {
      const { error } = await supabase
        .from("subscription_plans")
        .delete()
        .eq("id", planId);
      
      if (error) throw error;
      
      await fetchPlans();
      toast({
        title: "Erfolgreich",
        description: "Preisplan gelöscht.",
      });
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast({
        title: "Fehler",
        description: "Preisplan konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature("");
    }
  };

  const removeFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Preis- & Planverwaltung</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 px-1 sm:px-0">
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6" />
              Preis- & Planverwaltung
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Erstellen und verwalten Sie Preispläne für Ihre Plattform
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openEditDialog()} size="sm" className="self-start sm:self-auto">
                <Plus className="h-4 w-4 mr-2" />
                Neuer Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingPlan ? "Plan bearbeiten" : "Neuen Plan erstellen"}
                </DialogTitle>
                <DialogDescription>
                  Konfigurieren Sie die Details des Preisplans.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Planname</Label>
                    <Input {...register("name")} placeholder="z.B. Basic" />
                    {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="slug">Slug</Label>
                    <Input {...register("slug")} placeholder="z.B. basic" />
                    {errors.slug && <p className="text-sm text-red-500">{errors.slug.message}</p>}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea {...register("description")} placeholder="Beschreibung des Plans" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price_monthly">Monatspreis (€)</Label>
                    <Input 
                      {...register("price_monthly", { valueAsNumber: true })} 
                      type="number" 
                      step="0.01"
                      placeholder="0.00" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="price_yearly">Jahrespreis (€)</Label>
                    <Input 
                      {...register("price_yearly", { valueAsNumber: true })} 
                      type="number" 
                      step="0.01"
                      placeholder="0.00" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="max_customers">Max. Kunden</Label>
                    <Input 
                      {...register("max_customers", { valueAsNumber: true })} 
                      type="number" 
                      placeholder="Unbegrenzt" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_stamps_per_month">Max. Stempel/Monat</Label>
                    <Input 
                      {...register("max_stamps_per_month", { valueAsNumber: true })} 
                      type="number" 
                      placeholder="Unbegrenzt" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_referrals_per_month">Max. Empfehlungen/Monat</Label>
                    <Input 
                      {...register("max_referrals_per_month", { valueAsNumber: true })} 
                      type="number" 
                      placeholder="Unbegrenzt" 
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="trial_days">Testphase (Tage)</Label>
                  <Input 
                    {...register("trial_days", { valueAsNumber: true })} 
                    type="number" 
                    placeholder="14" 
                  />
                </div>

                <div>
                  <Label>Features</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        placeholder="Feature hinzufügen..."
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                      />
                      <Button type="button" onClick={addFeature} variant="outline" size="sm">
                        Hinzufügen
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {features.map((feature, index) => (
                        <Badge key={index} variant="secondary" className="cursor-pointer text-xs" onClick={() => removeFeature(index)}>
                          {feature} ×
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch {...register("is_active")} />
                  <Label>Plan ist aktiv</Label>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="order-2 sm:order-1">
                    Abbrechen
                  </Button>
                  <Button type="submit" className="order-1 sm:order-2">
                    {editingPlan ? "Aktualisieren" : "Erstellen"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
        {/* Plans Overview - Mobile Optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {plans.map((plan) => {
            const currentPlan = getCurrentPlan();
            const isCurrentPlan = currentPlan?.product_id === `prod_${plan.slug}` || 
                                 currentPlan?.name.toLowerCase().includes(plan.name.toLowerCase());
            const planKey = plan.slug.toLowerCase();
            
            return (
              <div key={plan.id} className={`border rounded-lg p-3 sm:p-4 relative ${isCurrentPlan ? 'border-primary bg-primary/5' : ''}`}>
                {isCurrentPlan && (
                  <div className="absolute -top-2 left-3 sm:left-4 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium">
                    Aktueller Plan
                  </div>
                )}
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-sm sm:text-base">{plan.name}</h3>
                  {plan.is_active ? (
                    <Badge variant="default" className="text-xs">Aktiv</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Inaktiv</Badge>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Euro className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="text-base sm:text-lg font-bold">{plan.price_monthly}€/Monat</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                    <Euro className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    <span>{plan.price_yearly}€/Jahr</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>{plan.trial_days} Tage Trial</span>
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    {plan.usage_count} aktive Abos
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 sm:gap-2 mt-3 sm:mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openEditDialog(plan)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deletePlan(plan.id)}
                    className="text-red-600 h-8 w-8 p-0"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  {/* Checkout Button for non-free plans */}
                  {plan.price_monthly > 0 && planKey !== 'free' && !isCurrentPlan && (
                    <Button 
                      size="sm"
                      onClick={() => createCheckout(planKey)}
                      disabled={subscriptionStatus.loading}
                      className="ml-auto text-xs px-2 h-8"
                    >
                      {subscriptionStatus.loading ? "..." : "Upgrade"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile-Optimized Details Table */}
        <div className="block sm:hidden space-y-3">
          {plans.map((plan) => (
            <Card key={plan.id} className="p-3">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-sm">{plan.name}</h3>
                    <p className="text-xs text-muted-foreground">{plan.description}</p>
                  </div>
                  {plan.is_active ? (
                    <Badge variant="default" className="text-xs">Aktiv</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Inaktiv</Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Preise</p>
                    <p className="text-sm">{plan.price_monthly}€/Monat</p>
                    <p className="text-xs text-muted-foreground">{plan.price_yearly}€/Jahr</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Aktive Abos</p>
                    <Badge variant="outline" className="text-xs">{plan.usage_count}</Badge>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Limits</p>
                  <div className="text-xs space-y-1">
                    <div>Kunden: {plan.max_customers || "∞"}</div>
                    <div>Stempel: {plan.max_stamps_per_month || "∞"}/Monat</div>
                    <div>Empfehlungen: {plan.max_referrals_per_month || "∞"}/Monat</div>
                    <div>Features: {Array.isArray(plan.features) ? plan.features.length : 0}</div>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openEditDialog(plan)}
                    className="flex-1 text-xs"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Bearbeiten
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deletePlan(plan.id)}
                    className="text-red-600 flex-1 text-xs"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Löschen
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:block border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Preise</TableHead>
                <TableHead>Limits</TableHead>
                <TableHead>Aktive Abos</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-sm text-muted-foreground">{plan.description}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div>{plan.price_monthly}€/Monat</div>
                      <div className="text-sm text-muted-foreground">{plan.price_yearly}€/Jahr</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>Kunden: {plan.max_customers || "∞"}</div>
                      <div>Stempel: {plan.max_stamps_per_month || "∞"}/Monat</div>
                      <div>Empfehlungen: {plan.max_referrals_per_month || "∞"}/Monat</div>
                      <div>Features: {Array.isArray(plan.features) ? plan.features.length : 0}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{plan.usage_count}</Badge>
                  </TableCell>
                  <TableCell>
                    {plan.is_active ? (
                      <Badge variant="default">Aktiv</Badge>
                    ) : (
                      <Badge variant="secondary">Inaktiv</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openEditDialog(plan)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => deletePlan(plan.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }